import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { useAuth } from '../../../context/AuthContext';
import RexiPopup from '../../../components/RexiPopup';
import SmartPagination from '../../../components/SmartPagination';
import '../DataTableTemplate.css';
import './Products.css';
import AddEditProductModal from "./AddEditProductModal";

interface Product {
  id: number;
  upc?: number;
  version?: string;
  name?: string;
  certificate_type: string;
  status: 'active' | 'inactive';
  inserted_at: string;
  updated_at: string;
}

// Moved outside component to prevent recreation on every render
const FilterInput = ({ label, placeholder, column, value, onChange }: {
  label: string;
  placeholder: string;
  column: string;
  value: string;
  onChange: (column: string, value: string) => void;
}) => (
  <div className="filter-group">
    <label className="filter-label">{label}</label>
    <input
      type="text"
      className="filter-input"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(column, e.target.value)}
    />
  </div>
);

const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columnFilters, setColumnFilters] = useState({
    name: '',
    upc: '',
    certificate_type: 'all',
    status: 'all'
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [popupState, setPopupState] = useState({
    help: false,
    delete: false,
    modal: false,
    foreignKeyError: false
  });
  const [productToDelete, setProductToDelete] = useState(null);

  const [editingProduct, setEditingProduct] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    upc: '',
    certificate_type: 'CPC',
    status: 'active' as 'active' | 'inactive'
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Fetch products from Supabase
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('auth_id', user?.id)
        .order('inserted_at', { ascending: false });

      if (error) {
        throw error;
      }

      setProducts(data || []);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Helper functions for popup management
  const togglePopup = (popup: keyof typeof popupState, show: boolean) => {
    setPopupState((prev: typeof popupState) => ({ ...prev, [popup]: show }));
  };

  // Table configuration - drives both headers and data rendering
  const tableColumns = [
    { key: 'upc', label: 'UPC', sortable: false, className: 'upc', showInTable: true },
    { key: 'name', label: 'Product Name', sortable: false, className: 'name', showInTable: true },
    { key: 'certificate_type', label: 'Certificate Type', sortable: true, className: 'certificate_type', showInTable: true },
    { key: 'status', label: 'Status', sortable: true, className: 'status', showInTable: true },
    { key: 'actions', label: 'Actions', sortable: false, className: 'actions', showInTable: true }
  ];

  // Filter products based on column filters
  const filteredProducts = products.filter((product: Product) => {
    const matchesName = !columnFilters.name || 
      product.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
    
    const matchesUPC = !columnFilters.upc || 
      product.upc?.toString().includes(columnFilters.upc);
    
    const matchesCertificateType = columnFilters.certificate_type === 'all' || 
      product.certificate_type === columnFilters.certificate_type;
    
    const matchesStatus = columnFilters.status === 'all' || product.status === columnFilters.status;
    
    return matchesName && matchesUPC && matchesCertificateType && matchesStatus;
  });

  const handleColumnFilterChange = (column: string, value: string) => {
    setColumnFilters((prev: typeof columnFilters) => ({ ...prev, [column]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearAllFilters = () => {
    setColumnFilters({
      name: '',
      upc: '',
      certificate_type: 'all',
      status: 'all'
    });
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reusable interactive cell component
  const InteractiveCell = ({ children, className = "", dataLabel = "" }: any) => (
    <div className={`table-cell ${className}`} data-label={dataLabel}>
      <div
        className="table-cell-input"
        contentEditable
        suppressContentEditableWarning={true}
        onInput={(e: any) => e.preventDefault()}
        onKeyDown={(e: any) => {
          const allowedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab', 'Escape'];
          const isCopyOperation = e.ctrlKey && (e.key === 'c' || e.key === 'a');
          if (!allowedKeys.includes(e.key) && !isCopyOperation) {
            e.preventDefault();
          }
        }}
        onPaste={(e: any) => e.preventDefault()}
        onMouseDown={(e: any) => e.stopPropagation()}
        onBlur={(e: any) => e.currentTarget.scrollLeft = 0}
      >
        {children}
      </div>
    </div>
  );


  // Reusable table header cell component
  const TableHeaderCell = ({ column }: any) => (
    <div className={`table-cell ${column.className || ''}`}>
      {column.sortable ? (
        <div className="sortable-header" onClick={() => handleSort(column.key)}>
          {column.label}
          <span className={`sort-icon ${getSortIcon(column.key)} ${sortConfig.key === column.key ? 'active' : ''}`}></span>
        </div>
      ) : (
        column.label
      )}
    </div>
  );

  // Reusable table data cell component
  const TableDataCell = ({ column, product }: any) => {
    if (column.key === 'actions') {
      return (
        <div className="table-cell" data-label="Actions">
          <div className="table-actions">
            <button className="table-action-btn table-edit-btn" onClick={() => openEditModal(product)}>
              Details
            </button>
            <button className="table-action-btn table-delete-btn" onClick={() => confirmDelete(product)}>
              Delete
            </button>
          </div>
        </div>
      );
    }

    if (column.key === 'status') {
      return (
        <div className="table-cell status" data-label="Status">
          {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
        </div>
      );
    }

    // Regular interactive cell
    return (
      <InteractiveCell className={column.className || ''} dataLabel={column.label}>
        {(product as any)[column.key] || '-'}
      </InteractiveCell>
    );
  };

  const hasActiveFilters = Object.values(columnFilters).some(filter => filter && filter !== 'all');

  // Sorting functionality
  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting to filtered results
  const sortedProducts = useMemo(() => {
    if (!sortConfig.key) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      if (sortConfig.key === 'inserted_at' || sortConfig.key === 'updated_at') {
        aValue = new Date(a[sortConfig.key]).getTime();
        bValue = new Date(b[sortConfig.key]).getTime();
      } else {
        aValue = (a[sortConfig.key] || '').toString().toLowerCase();
        bValue = (b[sortConfig.key] || '').toString().toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredProducts, sortConfig]);

  // Apply pagination to sorted results
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedProducts.slice(startIndex, endIndex);
  }, [sortedProducts, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const totalItems = sortedProducts.length;

  const getSortIcon = (columnKey: string) => {
    if (sortConfig.key !== columnKey) {
      return 'none';
    }
    return sortConfig.direction;
  };

  // Handle delete product
  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productToDelete.id)
        .eq('auth_id', user?.id);

      if (error) {
        // Check if it's a foreign key constraint error
        if (error.message.includes('violates foreign key constraint') && 
            error.message.includes('test_report_products_product_id_fkey')) {
          // Close delete confirmation popup
          togglePopup('delete', false);
          setProductToDelete(null);
          // Show custom foreign key error popup
          togglePopup('foreignKeyError', true);
          return;
        }
        throw error;
      }

      setProducts(products.filter((p: Product) => p.id !== productToDelete.id));
      togglePopup('delete', false);
      setProductToDelete(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error deleting product:', err);
    }
  };

  const confirmDelete = (product: Product) => {
    setProductToDelete(product);
    togglePopup('delete', true);
  };

  // Modal functions
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      upc: '',
      certificate_type: 'CPC',
      status: 'active'
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      upc: product.upc?.toString() || '',
      certificate_type: product.certificate_type || 'CPC',
      status: product.status
    });
    setFormErrors({});
    togglePopup('modal', true);
  };

  const closeModal = () => {
    togglePopup('modal', false);
    setEditingProduct(null);
    setFormErrors({});
    setSaving(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev: Record<string, string>) => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields validation
    const requiredFields = [
      { field: 'upc', label: 'UPC' },
      { field: 'name', label: 'Product name' },
      { field: 'certificate_type', label: 'Certificate type' }
    ];
    
    requiredFields.forEach(({ field, label }) => {
      if (!formData[field]?.trim()) {
        errors[field] = `${label} is required`;
      }
    });
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProduct = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      const productData = {
        name: formData.name || null,
        upc: formData.upc ? parseInt(formData.upc) : null,
        certificate_type: formData.certificate_type,
        status: formData.status,
        auth_id: user?.id
      };

      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id)
          .eq('auth_id', user?.id);

        if (error) throw error;

        setProducts(products.map((p: Product) => 
          p.id === editingProduct.id 
            ? { ...p, ...productData, updated_at: new Date().toISOString() }
            : p
        ));
      } else {
        // Create new product
        const { data, error } = await supabase
          .from('products')
          .insert([productData])
          .select()
          .single();

        if (error) throw error;

        setProducts([data, ...products]);
      }

      closeModal();
    } catch (err: any) {
      setError(err.message);
      console.error('Error saving product:', err);
    } finally {
      setSaving(false);
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'active': return 'status-active';
      case 'pending': return 'status-pending';
      case 'inactive': return 'status-inactive';
      default: return 'status-active';
    }
  };

  const handleExportCSV = () => {
    // Use filtered products (what's currently shown on page)
    const dataToExport = sortedProducts;
    
    if (dataToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define headers from table configuration (excluding actions)
    const csvColumns = tableColumns.filter(col => col.key !== 'actions');
    const headers = csvColumns.map(col => col.label);

    // Convert data to CSV format using table configuration
    const csvContent = [
      headers.join(','),
      ...dataToExport.map((product: Product) => 
        csvColumns.map(col => `"${(product as any)[col.key] || ''}"`)
          .join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `products_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="data-table-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <h1 className="data-table-title">Products</h1>
      <div className="page-help-link" onClick={() => togglePopup('help', true)}>
        Ask Rexi about this page
      </div>

      {error && (
        <div className="error-state">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="products-table-container">
        {/* Filter Toggle and Card */}
        <div className="products-filter-section">
        <div className="filter-toggle-header">
          <button 
            className="filter-toggle-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '▼' : '▶'} Filter Products
            {hasActiveFilters && <span className="active-filter-indicator">({Object.values(columnFilters).filter(filter => filter && filter !== 'all').length} active)</span>}
          </button>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearAllFilters}>
              Clear All Filters
            </button>
          )}
        </div>
        
        {showFilters && (
          <div className="table-filters">
            <div className="filters-grid">
              <FilterInput 
                label="UPC" 
                placeholder="Search by UPC..." 
                column="upc" 
                value={columnFilters.upc}
                onChange={handleColumnFilterChange}
              />
              <FilterInput 
                label="Product Name" 
                placeholder="Search by product name..." 
                column="name" 
                value={columnFilters.name}
                onChange={handleColumnFilterChange}
              />
              <div className="filter-group">
                <label className="filter-label">Certificate Type</label>
                <select
                  className="filter-select"
                  value={columnFilters.certificate_type}
                  onChange={(e) => handleColumnFilterChange('certificate_type', e.target.value)}
                >
                  <option value="all">All Certificate Types</option>
                  <option value="CPC">CPC</option>
                  <option value="GCC">GCC</option>
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  className="filter-select"
                  value={columnFilters.status}
                  onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Controls */}
      <div className="products-table-header-controls">
        <button className="add-data-btn" onClick={openAddModal}>
          + Add Product
        </button>
        <button className="export-csv-btn" onClick={handleExportCSV}>
          Export CSV
        </button>
      </div>

      {/* Product Table */}
      <div className="products-table-wrapper">
        <div className="products-table-scrollable">
          <div className="products-data-table">
          <div className="table-header">
            <div className="products-table-row">
              {tableColumns.filter(col => col.showInTable).map((column) => (
                <TableHeaderCell key={column.key} column={column} />
              ))}
            </div>
          </div>
          
          {sortedProducts.length === 0 ? (
            <div className="products-table-row">
              <div className="empty-table-message">
                {hasActiveFilters ? (
                  <>
                    <h4>No products found</h4>
                    <p>Try adjusting your filter criteria.</p>
                  </>
                ) : (
                  <p style={{color: '#999', fontStyle: 'italic'}}>No products yet</p>
                )}
              </div>
            </div>
          ) : (
            paginatedProducts.map((product: Product) => (
              <div key={product.id} className="products-table-row">
                {tableColumns.filter(col => col.showInTable).map((column) => (
                  <TableDataCell key={column.key} column={column} product={product} />
                ))}
              </div>
            ))
          )}
          </div>
        </div>
      </div>

      {/* Pagination */}
      <SmartPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        itemType="products"
        onPageChange={handlePageChange}
      />
      </div>

      {/* Help Popup */}
      {popupState.help && (
        <RexiPopup
          message="Manage your product catalog here, this is the product-specific data that is required for CPSC eFiling."
          onClose={() => togglePopup('help', false)}
          confirmText="Got It"
        />
      )}

      {/* Delete Confirmation Popup */}
      {popupState.delete && productToDelete && (
        <RexiPopup
          message="Are you sure you want to delete this record? This action cannot be undone."
          onClose={() => {
            togglePopup('delete', false);
            setProductToDelete(null);
          }}
          onContinue={handleDeleteProduct}
          showCancel={true}
        />
      )}

      {/* Foreign Key Constraint Error Popup */}
      {popupState.foreignKeyError && (
        <RexiPopup
          message="This product cannot be deleted because it is currently being used in one or more test reports. To delete this product, you must first remove it from all test reports that reference it."
          onClose={() => togglePopup('foreignKeyError', false)}
          confirmText="Understood"
        />
      )}

      {/* Add/Edit Product Modal */}

      <AddEditProductModal
        isOpen={popupState.modal}
        editingProduct={editingProduct}
        formData={formData}
        formErrors={formErrors}
        saving={saving}
        onClose={closeModal}
        handleInputChange={handleInputChange}
        handleSaveProduct={handleSaveProduct}
      />

    </div>
  );
};

export default Products;